import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { X, LoaderCircle } from "lucide-react";

import { useUserPopsViews } from "../../saved/users/hooks/useUserPopsViews";
import { useUserSavedPops } from "../../saved/users/hooks/useUserSavedPops";

import PopThumbnailCard from "../../../../components/cards/pops/PopThumbnailCard";

import "./CreatorPopsFeed.css";

const VIEW_TRACK_SECONDS = 3;
const MAX_VIEW_IDS_PER_REQUEST = 50;
const RENDER_DISTANCE = 1;

const validId = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const id = Number(value);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const normalizeCount = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const count = Number(value);

  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

const normalizeViewResult = (result) => {
  const popId = validId(result?.popId ?? result?.pop_id);

  if (!popId) return null;

  const realViews = normalizeCount(
    result.realViews ?? result.real_views ?? result.views,
  );

  const demoViews = normalizeCount(result.demoViews ?? result.demo_views);

  return {
    popId,
    realViews,
    demoViews,
    totalViews: normalizeCount(
      result.totalViews ?? result.total_views,
      realViews + demoViews,
    ),
  };
};

export default function CreatorPopsFeed({
  pops = [],
  initialPopId = null,
  fullscreen = true,
  onClose,
  onCreatorClick,
  onShare,
  onView,
  onMoreClick,
  onVideoError,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  showCreator = true,
  showActions = true,
}) {
  const { recordPopView, getPopViewCounts, getPopViewCount } =
    useUserPopsViews();

  const {
    isPopSaved,
    isPopSaving,
    hasSavedStatus,
    getPopSavedStatus,
    toggleSavePop,
  } = useUserSavedPops();

  const popItems = useMemo(() => {
    const seen = new Set();

    return (Array.isArray(pops) ? pops : []).filter((pop) => {
      const id = validId(pop?.popId);

      if (!id || seen.has(id)) return false;

      seen.add(id);
      return true;
    });
  }, [pops]);

  const initialIndex = Math.max(
    0,
    popItems.findIndex((pop) => String(pop.popId) === String(initialPopId)),
  );

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [muted, setMuted] = useState(true);
  const [viewCounts, setViewCounts] = useState({});

  const containerRef = useRef(null);
  const loadTriggerRef = useRef(null);

  const initializedRef = useRef(false);
  const mountedRef = useRef(false);
  const generationRef = useRef(0);
  const activeIndexRef = useRef(initialIndex);

  const loadingMoreRef = useRef(loadingMore);
  const requestingMoreRef = useRef(false);

  const loadedViewsRef = useRef(new Set());
  const pendingViewsRef = useRef(new Set());
  const pendingSavedRef = useRef(new Set());

  const trackedViewsRef = useRef(new Set());
  const pendingRecordsRef = useRef(new Set());
  const watchProgressRef = useRef(new Map());

  const callbacksRef = useRef({});

  callbacksRef.current = {
    onClose,
    onView,
    onLoadMore,
    recordPopView,
    getPopViewCounts,
    getPopSavedStatus,
    hasSavedStatus,
  };

  loadingMoreRef.current = loadingMore;

  const activePop = popItems[activeIndex] || null;

  const visiblePops = useMemo(() => {
    const start = Math.max(0, activeIndex - RENDER_DISTANCE);
    const end = Math.min(popItems.length, activeIndex + RENDER_DISTANCE + 1);

    return popItems.slice(start, end);
  }, [popItems, activeIndex]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (!popItems.length) return;

    if (activeIndexRef.current >= popItems.length) {
      const index = popItems.length - 1;

      activeIndexRef.current = index;
      setActiveIndex(index);
    }
  }, [popItems.length]);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container || !popItems.length || initializedRef.current) {
      return;
    }

    const targetIndex = Math.max(
      0,
      popItems.findIndex((pop) => String(pop.popId) === String(initialPopId)),
    );

    const target = container.children[targetIndex];

    if (!target) return;

    container.scrollTop = target.offsetTop - container.offsetTop;

    activeIndexRef.current = targetIndex;
    setActiveIndex(targetIndex);

    initializedRef.current = true;
  }, [popItems, initialPopId]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !popItems.length) return;

    const items = container.querySelectorAll(
      ".creator-pops-feed__item[data-pop-index]",
    );

    const ratios = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.dataset.popIndex);

          ratios.set(index, entry.isIntersecting ? entry.intersectionRatio : 0);
        });

        let nextIndex = activeIndexRef.current;
        let highestRatio = 0;

        ratios.forEach((ratio, index) => {
          if (ratio > highestRatio) {
            highestRatio = ratio;
            nextIndex = index;
          }
        });

        if (highestRatio < 0.55 || nextIndex === activeIndexRef.current) {
          return;
        }

        activeIndexRef.current = nextIndex;
        setActiveIndex(nextIndex);
      },
      {
        root: container,
        threshold: [0, 0.25, 0.55, 0.75, 1],
      },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [popItems.length]);

  useEffect(() => {
    if (!visiblePops.length) return;

    const generation = generationRef.current;

    const ids = [
      ...new Set(visiblePops.map((pop) => validId(pop.popId)).filter(Boolean)),
    ];

    const missingIds = ids.filter((id) => {
      const key = String(id);

      return (
        !loadedViewsRef.current.has(key) && !pendingViewsRef.current.has(key)
      );
    });

    if (!missingIds.length) return;

    const batches = [];

    for (
      let index = 0;
      index < missingIds.length;
      index += MAX_VIEW_IDS_PER_REQUEST
    ) {
      batches.push(missingIds.slice(index, index + MAX_VIEW_IDS_PER_REQUEST));
    }

    missingIds.forEach((id) => {
      pendingViewsRef.current.add(String(id));
    });

    batches.forEach(async (batch) => {
      try {
        const results = await callbacksRef.current.getPopViewCounts(batch);

        if (
          !mountedRef.current ||
          generation !== generationRef.current ||
          !Array.isArray(results)
        ) {
          return;
        }

        const nextCounts = {};

        results.forEach((result) => {
          const normalized = normalizeViewResult(result);

          if (!normalized) return;

          const key = String(normalized.popId);

          nextCounts[key] = normalized;
          loadedViewsRef.current.add(key);
        });

        if (Object.keys(nextCounts).length) {
          setViewCounts((previous) => ({
            ...previous,
            ...nextCounts,
          }));
        }
      } catch (error) {
        console.error("Unable to load Pop views:", error);
      } finally {
        if (generation === generationRef.current) {
          batch.forEach((id) => {
            pendingViewsRef.current.delete(String(id));
          });
        }
      }
    });
  }, [visiblePops]);

  useEffect(() => {
    visiblePops.forEach((pop) => {
      const popId = validId(pop.popId);

      if (!popId) return;

      const key = String(popId);

      if (
        callbacksRef.current.hasSavedStatus(popId) ||
        pendingSavedRef.current.has(key)
      ) {
        return;
      }

      pendingSavedRef.current.add(key);

      Promise.resolve()
        .then(() => callbacksRef.current.getPopSavedStatus(popId))
        .catch((error) => {
          console.error("Unable to load Pop saved status:", error);
        })
        .finally(() => {
          pendingSavedRef.current.delete(key);
        });
    });
  }, [visiblePops]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const videos = container.querySelectorAll("video");

    videos.forEach((video) => {
      const item = video.closest(".creator-pops-feed__item");

      const index = Number(item?.dataset.popIndex);
      const isActive = index === activeIndex;

      video.muted = muted;
      video.playsInline = true;

      if (!isActive || document.hidden) {
        video.pause();
        return;
      }

      const play = async () => {
        try {
          await video.play();
        } catch (error) {
          if (error?.name === "AbortError") return;

          if (!video.muted) {
            video.muted = true;

            try {
              await video.play();
            } catch (retryError) {
              if (retryError?.name !== "AbortError") {
                console.warn("Unable to play creator Pop:", retryError);
              }
            }
          }
        }
      };

      void play();
    });
  }, [activeIndex, muted, visiblePops]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const container = containerRef.current;

      if (!container) return;

      container.querySelectorAll("video").forEach((video) => {
        const item = video.closest(".creator-pops-feed__item");

        const isActive =
          Number(item?.dataset.popIndex) === activeIndexRef.current;

        if (document.hidden || !isActive) {
          video.pause();
        } else {
          video.play().catch(() => {});
        }
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const activeId = String(activePop?.popId ?? "");

    watchProgressRef.current.forEach((progress, key) => {
      if (key !== activeId) {
        watchProgressRef.current.set(key, {
          ...progress,
          lastTime: null,
        });
      }
    });
  }, [activePop?.popId]);

  useEffect(() => {
    const container = containerRef.current;
    const trigger = loadTriggerRef.current;

    if (
      !container ||
      !trigger ||
      !hasMore ||
      typeof callbacksRef.current.onLoadMore !== "function"
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      async (entries) => {
        if (
          !entries[0]?.isIntersecting ||
          loadingMoreRef.current ||
          requestingMoreRef.current
        ) {
          return;
        }

        requestingMoreRef.current = true;

        try {
          await callbacksRef.current.onLoadMore?.();
        } catch (error) {
          console.error("Unable to load more Pops:", error);
        } finally {
          requestingMoreRef.current = false;
        }
      },
      {
        root: container,
        rootMargin: "150% 0px",
        threshold: 0,
      },
    );

    observer.observe(trigger);

    return () => observer.disconnect();
  }, [hasMore, popItems.length]);

  useEffect(() => {
    if (!fullscreen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        callbacksRef.current.onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [fullscreen]);

  const handleTimeUpdate = (event, pop) => {
    const popId = validId(pop.popId);

    if (
      !popId ||
      document.hidden ||
      String(activePop?.popId) !== String(popId)
    ) {
      return;
    }

    const video = event.target;

    if (!(video instanceof HTMLVideoElement)) return;

    if (video.paused || video.seeking || video.readyState < 2) {
      return;
    }

    const currentTime = Number(video.currentTime);

    if (!Number.isFinite(currentTime)) return;

    const key = String(popId);

    const previous = watchProgressRef.current.get(key) || {
      lastTime: null,
      watchSeconds: 0,
    };

    let watchSeconds = previous.watchSeconds;

    if (previous.lastTime !== null) {
      const delta = currentTime - previous.lastTime;

      if (delta > 0 && delta <= 1.5) {
        watchSeconds += delta;
      }
    }

    watchProgressRef.current.set(key, {
      lastTime: currentTime,
      watchSeconds,
    });

    if (
      watchSeconds < VIEW_TRACK_SECONDS ||
      trackedViewsRef.current.has(key) ||
      pendingRecordsRef.current.has(key)
    ) {
      return;
    }

    pendingRecordsRef.current.add(key);

    const generation = generationRef.current;

    const duration = Number(video.duration);

    const completionPercent =
      Number.isFinite(duration) && duration > 0
        ? Math.min(100, (currentTime / duration) * 100)
        : 0;

    const stats = {
      watchSeconds: Number(watchSeconds.toFixed(2)),
      completionPercent: Number(completionPercent.toFixed(2)),
      referrerType: "creator",
    };

    Promise.resolve()
      .then(() => callbacksRef.current.recordPopView(popId, stats))
      .then(async (result) => {
        if (!result) return;

        if (!mountedRef.current || generation !== generationRef.current) {
          return;
        }

        trackedViewsRef.current.add(key);

        callbacksRef.current.onView?.(pop, stats);

        const results = await callbacksRef.current.getPopViewCounts([popId]);

        if (
          !mountedRef.current ||
          generation !== generationRef.current ||
          !Array.isArray(results)
        ) {
          return;
        }

        const nextCounts = {};

        results.forEach((result) => {
          const normalized = normalizeViewResult(result);

          if (normalized) {
            nextCounts[String(normalized.popId)] = normalized;
          }
        });

        setViewCounts((previous) => ({
          ...previous,
          ...nextCounts,
        }));
      })
      .catch((error) => {
        console.error("Pop view tracking failed:", error);
      })
      .finally(() => {
        pendingRecordsRef.current.delete(key);
      });
  };

  const handleSeeked = (popId) => {
    const key = String(popId);
    const progress = watchProgressRef.current.get(key);

    if (!progress) return;

    watchProgressRef.current.set(key, {
      ...progress,
      lastTime: null,
    });
  };

  const handleSavePop = async (pop) => {
    if (!validId(pop?.popId) || isPopSaving(pop.popId)) {
      return false;
    }

    return toggleSavePop(pop.popId);
  };

  const handleSharePop = async (pop) => {
    if (!pop?.popTag) return;

    if (onShare) {
      await onShare(pop);
      return;
    }

    const url = `${window.location.origin}/pops/${encodeURIComponent(pop.popTag)}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: pop.title || "Katch Pop",
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Unable to share Pop:", error);
      }
    }
  };

  const renderPop = (pop, index) => {
    const key = String(pop.popId);
    const active = index === activeIndex;

    const shouldRender = Math.abs(index - activeIndex) <= RENDER_DISTANCE;

    const fetched = viewCounts[key];

    const fallbackRealViews = normalizeCount(
      pop.realViews ?? pop.real_views ?? pop.views,
    );

    const realViews = fetched
      ? fetched.realViews
      : getPopViewCount(pop.popId, fallbackRealViews);

    const demoViews = fetched
      ? fetched.demoViews
      : normalizeCount(pop.demoViews ?? pop.demo_views);

    const totalViews = fetched
      ? fetched.totalViews
      : normalizeCount(
          pop.totalViews ?? pop.total_views,
          realViews + demoViews,
        );

    const displayPop = {
      ...pop,
      views: totalViews,
      realViews,
      demoViews,
      totalViews,
      isSaved: isPopSaved(pop.popId),
      videoUrl: pop.playbackUrl || pop.hlsUrl || pop.videoUrl || "",
    };

    return (
      <section
        key={key}
        data-pop-index={index}
        data-pop-id={key}
        className={`creator-pops-feed__item ${
          active ? "creator-pops-feed__item--active" : ""
        }`}
        onTimeUpdateCapture={(event) => {
          if (event.target?.tagName === "VIDEO") {
            handleTimeUpdate(event, pop);
          }
        }}
        onSeekedCapture={() => handleSeeked(pop.popId)}
        onErrorCapture={(event) => {
          if (event.target?.tagName === "VIDEO") {
            onVideoError?.(
              pop,
              event.target?.error || event.currentTarget?.error,
            );
          }
        }}
      >
        {shouldRender ? (
          <PopThumbnailCard
            pop={displayPop}
            mode="player"
            active={active}
            muted={muted}
            onMuteChange={setMuted}
            onClick={() => {
              activeIndexRef.current = index;
              setActiveIndex(index);
            }}
            onCreatorClick={
              showCreator ? () => onCreatorClick?.(pop) : undefined
            }
            onEdit={onMoreClick ? () => onMoreClick(pop) : undefined}
            onShare={() => handleSharePop(pop)}
            onSave={() => handleSavePop(pop)}
            isSaved={isPopSaved(pop.popId)}
            saving={isPopSaving(pop.popId)}
            showActions={showActions}
          />
        ) : (
          <div className="creator-pops-feed__placeholder" aria-hidden="true" />
        )}
      </section>
    );
  };

  const content = (
    <div
      className={`creator-pops-feed__overlay ${
        fullscreen ? "creator-pops-feed__overlay--fullscreen" : ""
      }`}
      role="dialog"
      aria-modal={fullscreen}
      aria-label="Creator Pops player"
    >
      <div className="creator-pops-feed">
        <button
          type="button"
          className="creator-pops-feed__close"
          onClick={onClose}
          aria-label="Close Pops"
        >
          <X size={22} />
        </button>

        <div ref={containerRef} className="creator-pops-feed__container">
          {popItems.map(renderPop)}

          {loadingMore && (
            <div className="creator-pops-feed__load-more">
              <LoaderCircle size={22} className="creator-pops-feed__loader" />
              <span>Loading more Pops...</span>
            </div>
          )}

          {hasMore && (
            <div
              ref={loadTriggerRef}
              className="creator-pops-feed__load-trigger"
              aria-hidden="true"
            />
          )}

          {!hasMore && popItems.length > 0 && (
            <div className="creator-pops-feed__end">You're all caught up.</div>
          )}
        </div>
      </div>
    </div>
  );

  return fullscreen ? createPortal(content, document.body) : content;
}
